import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, CreditCard, QrCode, ArrowRight, User, HardDrive, Layers, FileText } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { SEO } from '../components/SEO';
import { usePublicSaasPlans } from '../hooks/usePublicSaasPlans';

type Period = 'mensal' | 'trimestral' | 'semestral' | 'anual';

const periods = [
  { id: 'mensal', label: 'Mensal', discount: 0, badge: null },
  { id: 'trimestral', label: 'Trimestral', discount: 0.05, badge: '-5%' },
  { id: 'semestral', label: 'Semestral', discount: 0.10, badge: '-10%' },
  { id: 'anual', label: 'Anual', discount: 0.15, badge: '-15%' },
] as const;

const discountMap: Record<Period, number> = {
  mensal: 0,
  trimestral: 0.05,
  semestral: 0.10,
  anual: 0.15,
};

export function Preco() {
  const navigate = useNavigate();
  const [activePeriod, setActivePeriod] = useState<Period>('mensal');
  const activeDiscount = discountMap[activePeriod];
  const { plans, enterprise, features, loading, isFeatureEnabled, usersLabelForTable, storageLabelForTable, invoicesLabelForTable, invoicesOverageLabelForTable, invoicesFiscalEmissionLabelForTable } =
    usePublicSaasPlans();

  const tableGridStyle = useMemo(
    () => ({ gridTemplateColumns: `2fr repeat(${plans.length}, minmax(4rem, 1fr))` }),
    [plans.length]
  );

  return (
    <div className="w-full bg-slate-50 min-h-screen pt-20 pb-24 font-sans">
      <SEO
        title="Planos e Preços | RosTech"
        description="Escolha o plano ideal para a sua oficina mecânica e tenha controle total do seu negócio em um só lugar."
        canonicalUrl="/preco"
      />

      <div className="max-w-7xl mx-auto px-6 text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight mb-4">
          Escolha o plano ideal para o seu negócio
        </h1>
        <p className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto mb-10">
          Transforme a gestão da sua oficina com a plataforma mais completa do mercado. Sem taxas ocultas, cancele quando quiser.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-4 mb-4">
          <Button size="lg" className="px-8">Comece agora gratuitamente</Button>
          <a href="https://wa.me/5524992818632" target="_blank" rel="noopener noreferrer">
            <Button size="lg" variant="outline" className="px-8">Falar com consultor</Button>
          </a>
        </div>
      </div>

      <div className="flex justify-center mb-16 px-4">
        <div className="inline-flex flex-wrap items-center justify-center bg-white p-2 rounded-3xl md:rounded-full shadow-sm border border-slate-200 max-w-full gap-1 lg:gap-2">
          {periods.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setActivePeriod(p.id as Period)}
              className={`relative flex items-center justify-center gap-2 px-5 md:px-6 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap sm:w-auto ${
                activePeriod === p.id
                  ? 'bg-blue-700 text-white shadow-md'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <span>{p.label}</span>
              {p.badge && (
                <span
                  className={`px-2 py-0.5 text-[10px] uppercase font-bold rounded-full transition-colors ${
                    activePeriod === p.id
                      ? 'bg-blue-600 text-white border border-blue-500/50'
                      : 'bg-green-100 text-green-700 border border-green-200/50'
                  }`}
                >
                  {p.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="text-center text-slate-500 mb-8">Carregando planos…</p>}

      <div className="max-w-7xl mx-auto px-6 mb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 xl:gap-8 justify-center">
          {plans.map((plan) => {
            const planDiscount = plan.eligiblePeriodDiscount ? activeDiscount : 0;
            const finalPrice = plan.price * (1 - planDiscount);
            return (
              <div
                key={plan.id}
                className={`group relative bg-white rounded-3xl border ${
                  plan.isPopular
                    ? 'border-blue-500 shadow-xl shadow-blue-900/5 ring-1 ring-blue-500'
                    : 'border-slate-200 shadow-sm'
                } p-8 flex flex-col hover:-translate-y-1 transition-transform`}
              >
                {plan.badge && (
                  <div className="absolute top-0 inset-x-0 h-10 flex items-center justify-center">
                    <div
                      className={`-mt-5 px-4 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-widest ${
                        plan.isPopular ? 'bg-blue-600 text-white' : 'bg-green-100 text-green-800 border border-green-200'
                      }`}
                    >
                      {plan.badge}
                    </div>
                  </div>
                )}

                <h3 className="text-xl font-bold text-slate-900 tracking-tight mb-2 mt-2">{plan.name}</h3>

                <div className="flex items-end gap-1 mb-1">
                  <span className="text-sm font-bold text-slate-500 mb-1.5">R$</span>
                  <span className="text-4xl font-extrabold text-slate-900 tracking-tighter">
                    {finalPrice.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className="text-sm text-slate-500 mb-1.5">/mês</span>
                </div>

                {planDiscount > 0 && activePeriod !== 'mensal' ? (
                  <p className="text-[11px] text-slate-500 mb-6 leading-relaxed">
                    Valor promocional para o 1º{' '}
                    {activePeriod === 'trimestral' ? 'trimestre' : activePeriod === 'semestral' ? 'semestre' : 'ano'}.
                    Após o período, o valor passa a ser{' '}
                    <strong>R$ {plan.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês</strong>.
                  </p>
                ) : (
                  <div className="mb-6 h-[33px]" />
                )}

                <ul className="space-y-4 flex-1 mb-8">
                  <li className="flex items-start gap-3">
                    <User className="w-5 h-5 text-blue-600 shrink-0" />
                    <span className="text-sm text-slate-700">{plan.users}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <HardDrive className="w-5 h-5 text-blue-600 shrink-0" />
                    <span className="text-sm text-slate-700">{plan.storage} de Armazenamento</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Layers className="w-5 h-5 text-blue-600 shrink-0" />
                    <span className="text-sm text-slate-700">
                      Módulos: <span className="font-semibold">{plan.modulesText}</span>
                    </span>
                  </li>
                </ul>

                <Button
                  variant={plan.isPopular ? 'default' : 'outline'}
                  className="w-full hover:bg-blue-700 hover:text-white hover:border-blue-700 transition-colors group-hover:bg-blue-700 group-hover:text-white group-hover:border-blue-700"
                  onClick={() => navigate(`/checkout?plan=${plan.id}&period=${activePeriod}`)}
                >
                  Assinar Plano
                </Button>
              </div>
            );
          })}

          <div className="relative bg-slate-900 rounded-3xl border border-slate-800 shadow-xl p-8 flex flex-col md:col-span-2 xl:col-span-1">
            <div className="absolute top-0 inset-x-0 h-10 flex items-center justify-center">
              <div className="-mt-5 px-4 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-widest bg-amber-400 text-amber-950">
                {enterprise.badge || 'Premium'}
              </div>
            </div>
            <h3 className="text-xl font-bold text-white tracking-tight mb-2 mt-2">{enterprise.name}</h3>
            <div className="flex items-end gap-1 mb-6">
              <span className="text-3xl lg:text-4xl font-extrabold text-white tracking-tighter mt-1">Sob consulta</span>
            </div>
            <p className="text-slate-400 text-sm mb-6 flex-1">
              Solução personalizada para grandes operações e redes de oficinas.
            </p>
            <ul className="space-y-4 flex-1 mb-8">
              <li className="flex items-start gap-3">
                <User className="w-5 h-5 text-amber-400 shrink-0" />
                <span className="text-sm text-slate-300">{enterprise.users}</span>
              </li>
              <li className="flex items-start gap-3">
                <HardDrive className="w-5 h-5 text-amber-400 shrink-0" />
                <span className="text-sm text-slate-300">{enterprise.storage}</span>
              </li>
              <li className="flex items-start gap-3">
                <Layers className="w-5 h-5 text-amber-400 shrink-0" />
                <span className="text-sm text-slate-300">
                  Módulos: <span className="font-semibold text-white">{enterprise.modulesText}</span>
                </span>
              </li>
            </ul>
            <a href="https://wa.me/5524992818632" target="_blank" rel="noopener noreferrer">
              <Button variant="default" className="w-full bg-white text-slate-950 hover:bg-slate-200">
                Falar com Vendas
              </Button>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 mb-24">
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between shadow-sm">
          <div className="mb-6 sm:mb-0 text-center sm:text-left">
            <h3 className="text-lg font-bold text-slate-900">Formas de Pagamento Aceitas</h3>
            <p className="text-slate-500 text-sm mt-1">Facilitamos a contratação com opções flexíveis.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 px-4 py-2 rounded-lg">
              <CreditCard className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-semibold text-slate-700">Cartão de Crédito</span>
            </div>
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 px-4 py-2 rounded-lg">
              <QrCode className="w-5 h-5 text-teal-600" />
              <span className="text-sm font-semibold text-slate-700">PIX</span>
            </div>
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 px-4 py-2 rounded-lg">
              <FileText className="w-5 h-5 text-slate-600" />
              <span className="text-sm font-semibold text-slate-700">Boleto</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mb-24 overflow-x-auto">
        <h2 className="text-3xl font-extrabold text-slate-900 text-center mb-12">Comparação de Recursos</h2>

        {loading && (
          <p className="text-center text-slate-500 mb-6">Atualizando tabela de recursos…</p>
        )}

        <div className="min-w-[800px] border border-slate-200 rounded-3xl bg-white overflow-hidden shadow-sm">
          <div className="grid bg-slate-50 border-b border-slate-200 items-end" style={tableGridStyle}>
            <div className="p-6 text-left font-bold text-slate-900">Módulo / Recurso</div>
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`p-6 text-center font-bold text-slate-900 ${plan.isPopular ? 'bg-blue-50' : plan.badge ? 'bg-blue-50/50' : ''}`}
              >
                <span className="block text-sm leading-tight px-1">{plan.name}</span>
                {plan.badge && (
                  <span
                    className={`block text-[10px] uppercase font-extrabold mt-1 ${
                      plan.isPopular ? 'bg-blue-600 text-white rounded px-1 w-fit mx-auto' : 'text-green-600'
                    }`}
                  >
                    {plan.badge.length > 12 ? `${plan.badge.slice(0, 10)}…` : plan.badge}
                  </span>
                )}
              </div>
            ))}
          </div>

          <div className="divide-y divide-slate-100">
            <div className="grid hover:bg-slate-50/50 transition-colors" style={tableGridStyle}>
              <div className="p-4 px-6 text-sm font-medium text-slate-700 flex items-center">Usuários Inclusos</div>
              {plans.map((plan) => (
                <div
                  key={`u-${plan.id}`}
                  className={`p-4 text-center text-sm font-semibold text-slate-600 ${plan.isPopular ? 'bg-blue-50/50' : ''}`}
                >
                  {usersLabelForTable(plan.id)}
                </div>
              ))}
            </div>
            <div className="grid hover:bg-slate-50/50 transition-colors" style={tableGridStyle}>
              <div className="p-4 px-6 text-sm font-medium text-slate-700 flex items-center">Armazenamento</div>
              {plans.map((plan) => (
                <div
                  key={`s-${plan.id}`}
                  className={`p-4 text-center text-sm font-semibold text-slate-600 ${plan.isPopular ? 'bg-blue-50/50' : ''}`}
                >
                  {storageLabelForTable(plan.id)}
                </div>
              ))}
            </div>
            <div className="grid hover:bg-slate-50/50 transition-colors" style={tableGridStyle}>
              <div className="p-4 px-6 text-sm font-medium text-slate-700 flex items-center">Limite de Notas</div>
              {plans.map((plan) => (
                <div
                  key={`i-${plan.id}`}
                  className={`p-4 text-center text-sm font-semibold text-slate-600 ${plan.isPopular ? 'bg-blue-50/50' : ''}`}
                >
                  {invoicesLabelForTable(plan.id)}
                </div>
              ))}
            </div>
            <div className="grid hover:bg-slate-50/50 transition-colors" style={tableGridStyle}>
              <div className="p-4 px-6 text-sm font-medium text-slate-700 flex items-center">
                Após o limite, cada emissão por
              </div>
              {plans.map((plan) => (
                <div
                  key={`io-${plan.id}`}
                  className={`p-4 text-center text-sm font-semibold text-slate-600 ${plan.isPopular ? 'bg-blue-50/50' : ''}`}
                >
                  {invoicesOverageLabelForTable(plan.id)}
                </div>
              ))}
            </div>
            <div className="grid hover:bg-slate-50/50 transition-colors" style={tableGridStyle}>
              <div className="p-4 px-6 text-sm font-medium text-slate-700 flex items-center">
                Emissão Fiscal
              </div>
              {plans.map((plan) => (
                <div
                  key={`ife-${plan.id}`}
                  className={`p-4 text-center text-sm font-semibold text-slate-600 ${plan.isPopular ? 'bg-blue-50/50' : ''}`}
                >
                  {invoicesFiscalEmissionLabelForTable(plan.id)}
                </div>
              ))}
            </div>

            {features.map((feat) => (
              <div key={feat.key} className="grid hover:bg-slate-50/50 transition-colors" style={tableGridStyle}>
                <div className="p-4 px-6 text-sm font-medium text-slate-700 flex items-center">{feat.label}</div>
                {plans.map((plan) => {
                  const on = isFeatureEnabled(plan.id, feat.key);
                  return (
                    <div
                      key={`${plan.id}-${feat.key}`}
                      className={`p-4 flex items-center justify-center text-slate-400 ${plan.isPopular ? 'bg-blue-50/50' : ''}`}
                    >
                      {on ? (
                        <Check className={`w-5 h-5 ${plan.isPopular ? 'text-blue-600' : 'text-green-600'}`} />
                      ) : (
                        <X className="w-4 h-4 opacity-30" />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-3xl font-bold text-slate-900 mb-6">Ainda com dúvidas?</h2>
        <p className="text-slate-500 mb-8 max-w-2xl mx-auto leading-relaxed">
          Nossa equipe de especialistas está pronta para analisar o cenário da sua empresa e indicar o plano ideal para
          alavancar seus resultados com a RosTech.
        </p>
        <a href="https://wa.me/5524992818632" target="_blank" rel="noopener noreferrer">
          <Button size="lg" className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8">
            Falar com a Equipe Comercial <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </a>
      </div>
    </div>
  );
}
